import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Settings2, Check } from 'lucide-react';
import BusinessHoursEditor from './BusinessHoursEditor';

const AUTOMOTIVE_TYPES = [
  { value: 'concesionario_nuevos_y_seminuevos', label: 'Concesionario (nuevos y seminuevos)' },
  { value: 'agencia_solo_seminuevos', label: 'Agencia (solo seminuevos)' },
  { value: 'concesionario_motos', label: 'Concesionario de motocicletas' },
  { value: 'taller_postventa', label: 'Taller postventa' },
];

const RE_PROFILES = [
  { value: 'developer', label: 'Desarrollador inmobiliario' },
  { value: 'broker', label: 'Inmobiliaria / broker' },
];

export default function BusinessVerticalProfile() {
  const ctx = useOutletContext() || {};
  const dict = ctx.dict || {};
  const refetchDict = ctx.refetchDict;
  const vertical = dict.vertical || 'generic';
  const [config, setConfig] = useState(dict.vertical_config || {});
  const [saving, setSaving] = useState(false);
  const [businessHours, setBusinessHours] = useState(null);
  const [loadingHours, setLoadingHours] = useState(true);

  useEffect(() => {
    api.get('/tenant/business/business-hours')
      .then(r => setBusinessHours(r.data?.business_hours || {}))
      .catch(() => setBusinessHours({}))
      .finally(() => setLoadingHours(false));
  }, []);

  const saveVertical = async (updates) => {
    setSaving(true);
    try {
      const { data } = await api.patch('/tenant/business/vertical-config', updates);
      setConfig(data.vertical_config || {});
      refetchDict?.();
      toast.success('Perfil actualizado');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="vertical-profile">
      {/* Business Hours — visible for ALL verticals */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-4">
        {loadingHours ? (
          <div className="flex justify-center py-4"><div className="w-5 h-5 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" /></div>
        ) : (
          <BusinessHoursEditor initialHours={businessHours} onSaved={() => {
            api.get('/tenant/business/business-hours').then(r => setBusinessHours(r.data?.business_hours || {}));
          }} />
        )}
      </div>

      {/* Vertical-specific profile — only automotive/real_estate.
          Sprint 75 (6/N) hotfix: este perfil gatea módulos completos
          (p.ej. Desarrollos/ERP solo si business_profile=='developer'), así
          que una vez definido queda bloqueado — un clic accidental en la
          otra opción ya no puede desactivar en silencio un módulo en uso.
          El backend rechaza el cambio con 409 aunque alguien fuerce la
          llamada; aquí solo reflejamos ese candado deshabilitando el botón. */}
      {vertical === 'automotive' && (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 space-y-3">
          <h4 className="text-sm font-semibold text-[#0F172A]">Tipo de negocio automotriz</h4>
          {config.automotive_business_type && (
            <p className="text-xs text-[#64748B]">
              Este perfil ya quedó definido y no se puede cambiar por aquí — protege la configuración y los datos que ya creaste. Si necesitas cambiarlo, contacta a soporte.
            </p>
          )}
          <div className="grid gap-2">
            {AUTOMOTIVE_TYPES.map(t => {
              const locked = !!config.automotive_business_type;
              const selected = config.automotive_business_type === t.value;
              return (
                <button key={t.value} type="button" disabled={locked}
                  onClick={() => !locked && saveVertical({ automotive_business_type: t.value })}
                  className={`p-3 rounded-xl border text-left text-sm transition-all ${
                    selected ? 'border-[#0F172A] bg-[#F8FAFC] ring-1 ring-[#0F172A]' : 'border-[#E2E8F0]'} ${
                    locked ? 'opacity-60 cursor-not-allowed' : 'hover:border-[#CBD5E1]'}`}
                  data-testid={`auto-type-${t.value}`}>
                  {selected && <Check className="w-3.5 h-3.5 inline mr-1 text-emerald-600" />}
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {vertical === 'real_estate' && (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 space-y-3">
          <h4 className="text-sm font-semibold text-[#0F172A]">Perfil inmobiliario</h4>
          {config.business_profile && (
            <p className="text-xs text-[#64748B]">
              Este perfil ya quedó definido y no se puede cambiar por aquí — protege la configuración y los datos que ya creaste (por ejemplo, tus Desarrollos). Si necesitas cambiarlo, contacta a soporte.
            </p>
          )}
          <div className="grid gap-2">
            {RE_PROFILES.map(t => {
              const locked = !!config.business_profile;
              const selected = config.business_profile === t.value;
              return (
                <button key={t.value} type="button" disabled={locked}
                  onClick={() => !locked && saveVertical({ business_profile: t.value })}
                  className={`p-3 rounded-xl border text-left text-sm transition-all ${
                    selected ? 'border-[#0F172A] bg-[#F8FAFC] ring-1 ring-[#0F172A]' : 'border-[#E2E8F0]'} ${
                    locked ? 'opacity-60 cursor-not-allowed' : 'hover:border-[#CBD5E1]'}`}
                  data-testid={`re-profile-${t.value}`}>
                  {selected && <Check className="w-3.5 h-3.5 inline mr-1 text-emerald-600" />}
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!['automotive', 'real_estate'].includes(vertical) && (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 text-center" data-testid="vertical-profile-generic">
          <Settings2 className="w-6 h-6 text-[#94A3B8] mx-auto mb-2" />
          <p className="text-sm text-[#64748B]">Tu perfil de negocio no requiere configuración adicional de vertical</p>
        </div>
      )}
    </div>
  );
}
